import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const Navigation = ({ activeSection }) => {
  const { language, setLanguage } = useLanguage();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const languages = [
    { code: "en", label: "English" },
    { code: "zh", label: "中文" },
    { code: "ja", label: "日本語" },
  ];

  const getNavLabel = (section) => {
    const labels = {
      home: { en: "HOME", zh: "主页", ja: "ホーム" },
      projects: { en: "PROJECTS", zh: "项目", ja: "プロジェクト" },
      experience: { en: "EXPERIENCE", zh: "经历", ja: "経験" },
      cv: { en: "CV", zh: "履历", ja: "履歴書" },
      personal: { en: "PERSONAL", zh: "个人", ja: "個人" },
    };

    return labels[section][language] || labels[section].en;
  };

  // 监听滚动效果
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full backdrop-blur-md z-50 transition-all duration-300
                 ${scrolled ? "bg-white/80 shadow-md" : "bg-white/30"}`}
    >
      <div className="container mx-auto flex justify-between items-center py-4 px-6">
        <Link
          to="/"
          className="text-xl font-light tracking-wider text-neutral-800 transition-all hover:text-primary-500 flex items-center"
        >
          <img
            src="/logo.svg"
            alt="Mark Chen Logo"
            className="h-10 w-10 mr-2"
          />
          Mark Chen
        </Link>

        {/* 移动端汉堡菜单按钮 */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-neutral-700 hover:text-primary-500 focus:outline-none"
        >
          {mobileMenuOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>

        {/* 桌面端导航 */}
        <div className="hidden md:flex items-center space-x-6 md:space-x-8">
          <Link
            to="/"
            className={`text-sm tracking-wider transition-all ${
              activeSection === "home"
                ? "text-primary-500 font-medium"
                : "text-neutral-600 hover:text-primary-400"
            }`}
          >
            {getNavLabel("home")}
          </Link>
          <Link
            to="/projects"
            className={`text-sm tracking-wider transition-all ${
              activeSection === "projects"
                ? "text-primary-500 font-medium"
                : "text-neutral-600 hover:text-primary-400"
            }`}
          >
            {getNavLabel("projects")}
          </Link>
          <Link
            to="/experience"
            className={`text-sm tracking-wider transition-all ${
              activeSection === "experience"
                ? "text-primary-500 font-medium"
                : "text-neutral-600 hover:text-primary-400"
            }`}
          >
            {getNavLabel("experience")}
          </Link>
          <Link
            to="/personal"
            className={`text-sm tracking-wider transition-all ${
              activeSection === "personal"
                ? "text-primary-500 font-medium"
                : "text-neutral-600 hover:text-primary-400"
            }`}
          >
            {getNavLabel("personal")}
          </Link>

          <div className="relative ml-2">
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex items-center space-x-1 bg-white/80 hover:bg-white text-neutral-700 px-3 py-2 rounded-full transition-all shadow-sm hover:shadow-md"
            >
              <span className="text-sm">
                {language === "en" ? "EN" : language === "zh" ? "中" : "日"}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform duration-200 ${
                  isLangMenuOpen ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isLangMenuOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white/90 backdrop-blur-md rounded-2xl shadow-lg py-2 z-20 border border-neutral-100">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsLangMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm ${
                      language === lang.code
                        ? "text-primary-500 bg-primary-50/70"
                        : "text-neutral-700 hover:bg-neutral-50/70"
                    } transition-all`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md shadow-lg">
          <div className="px-6 py-4 space-y-4 flex flex-col">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-sm tracking-wider py-2 transition-all ${
                activeSection === "home"
                  ? "text-primary-500 font-medium"
                  : "text-neutral-600"
              }`}
            >
              {getNavLabel("home")}
            </Link>
            <Link
              to="/projects"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-sm tracking-wider py-2 transition-all ${
                activeSection === "projects"
                  ? "text-primary-500 font-medium"
                  : "text-neutral-600"
              }`}
            >
              {getNavLabel("projects")}
            </Link>
            <Link
              to="/experience"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-sm tracking-wider py-2 transition-all ${
                activeSection === "experience"
                  ? "text-primary-500 font-medium"
                  : "text-neutral-600"
              }`}
            >
              {getNavLabel("experience")}
            </Link>
            <Link
              to="/personal"
              onClick={() => setMobileMenuOpen(false)}
              className={`text-sm tracking-wider py-2 transition-all ${
                activeSection === "personal"
                  ? "text-primary-500 font-medium"
                  : "text-neutral-600"
              }`}
            >
              {getNavLabel("personal")}
            </Link>

            <div className="py-2 border-t border-neutral-100">
              <p className="text-sm text-neutral-500 mb-2">语言 / Language</p>
              <div className="flex space-x-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setMobileMenuOpen(false);
                    }}
                    className={`px-3 py-1 text-sm rounded-full ${
                      language === lang.code
                        ? "bg-primary-100 text-primary-500"
                        : "bg-neutral-100 text-neutral-700"
                    }`}
                  >
                    {lang.code === "en"
                      ? "EN"
                      : lang.code === "zh"
                      ? "中"
                      : "日"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
